class GiftQuiz extends HTMLElement {
  constructor() {
    super();

    this.steps = [...this.querySelectorAll('[data-step]')];
    this.priceRanges = [...this.querySelectorAll('.gift-quiz__price-range')];
    this.results = this.querySelector('.gift-quiz__results');
    this.track = this.querySelector('.gift-quiz__track');
    this.retake = this.querySelector('.gift-quiz__retake');
    this.recipientTitles = [...this.querySelectorAll('[data-gift-quiz-title]')];

    // Gift lists are moved into the results step when shown, so keep references
    // to them rather than querying the DOM each time.
    this.giftLists = new Map(
      [...this.querySelectorAll('[data-gifts-for]')].map((list) => [list.dataset.giftsFor, list])
    );

    this.selectedGiftList = null;
    this.isTransitioning = false;
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    if (this.isTransitioning) return;

    const giftOption = event.target.closest('[data-gift-option]');
    const priceRange = event.target.closest('.gift-quiz__price-range');
    const stepLink = event.target.closest('[data-go-to-step]');

    if (giftOption) this.#selectGiftOption(giftOption);
    else if (priceRange) this.#selectPriceRange(priceRange);
    else if (stepLink) this.#goToStep(stepLink.dataset.goToStep);
  };

  #selectGiftOption(giftOption) {
    this.selectedGiftList = this.giftLists.get(giftOption.dataset.giftOption);
    if (!this.selectedGiftList) return;

    const title = giftOption.textContent.trim();
    this.recipientTitles.forEach((recipientTitle) => (recipientTitle.textContent = title));

    const prices = this.#getGifts().map((gift) => Number(gift.dataset.price));
    let hasAvailableRange = false;

    this.priceRanges.forEach((priceRange) => {
      const isAvailable = prices.some((price) => this.#isInRange(price, priceRange));
      priceRange.classList.toggle('hidden', !isAvailable);
      hasAvailableRange ||= isAvailable;
    });

    if (hasAvailableRange) {
      this.#goToStep('price_ranges');
    } else {
      this.#showGifts(() => true);
      this.#goToStep('gift_products');
    }
  }

  #selectPriceRange(priceRange) {
    this.#showGifts((price) => this.#isInRange(price, priceRange));
    this.#goToStep('gift_products');
  }

  #getGifts() {
    return [...this.selectedGiftList.querySelectorAll('.gift-quiz__gift')];
  }

  // Prices are in pence. Lower limit is inclusive, upper limit exclusive, so
  // adjoining ranges (e.g. 0–20 and 20–40) never both include the same gift.
  #isInRange(price, priceRange) {
    const lower = Number(priceRange.dataset.lowerLimit) * 100;
    const upper = priceRange.dataset.upperLimit === '' ? Infinity : Number(priceRange.dataset.upperLimit) * 100;

    return price >= lower && price < upper;
  }

  #showGifts(isPriceIncluded) {
    this.#getGifts().forEach((gift) => {
      gift.classList.toggle('hidden', !isPriceIncluded(Number(gift.dataset.price)));
    });

    this.selectedGiftList.classList.remove('hidden');
    this.results.replaceChildren(this.selectedGiftList);
    this.results.scrollLeft = 0;
  }

  #goToStep(stepName) {
    const current = this.steps.find((step) => step.dataset.stepActive === 'true');
    const next = this.steps.find((step) => step.dataset.step === stepName);
    if (!next || next === current) return;

    // Steps sit side by side in DOM order, so moving to a later step slides left.
    const direction = this.steps.indexOf(next) > this.steps.indexOf(current) ? 'forward' : 'back';

    this.isTransitioning = true;
    this.track.style.height = `${this.track.offsetHeight}px`;
    this.classList.add('gift-quiz--transitioning', `gift-quiz--${direction}`);
    this.retake.classList.toggle('hidden', stepName !== 'gift_products');
    next.dataset.stepActive = 'true';

    this.track.style.height = `${next.offsetHeight}px`;

    // Durations come from the theme's CSS variables (and are zeroed for reduced motion).
    const duration = parseFloat(getComputedStyle(next).animationDuration) * 1000 || 0;

    setTimeout(() => {
      current.dataset.stepActive = 'false';
      this.classList.remove('gift-quiz--transitioning', `gift-quiz--${direction}`);
      this.track.style.height = '';
      this.isTransitioning = false;

      next.querySelector('.gift-quiz__step-heading')?.focus();
    }, duration);
  }
}

if (!customElements.get('gift-quiz')) {
  customElements.define('gift-quiz', GiftQuiz);
}
